import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaChevronLeft, FaChevronRight, FaExclamationTriangle } from "react-icons/fa";
import Loader from "../../components/Loader";
import { AuthContext } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { convertTimestampToFrenchFormat } from "../../scripts/utils";
import {
  getUbiquitiDashboardGraphs,
  getUbiquitiSites,
} from "../../scripts/api_ubiquiti";
import UbiquitiDefaultWifiSpeeds from "./UbiquitiDefaultWifiSpeeds";
import UbiquitiGatewaySummary from "./UbiquitiGatewaySummary";
import UbiquitiGraphPanel from "./UbiquitiGraphPanel";
import UbiquitiGraphToolbar from "./UbiquitiGraphToolbar";
import UbiquitiIspPanel from "./UbiquitiIspPanel";
import UbiquitiMostCommonDevices from "./UbiquitiMostCommonDevices";
import UbiquitiTopOverview from "./UbiquitiTopOverview";
import {
  buildUbiquitiWifiSeries,
  getUbiquitiFrequencyProfile,
  getUbiquitiDataArray,
  getUbiquitiHealthScore,
  getUbiquitiWanRows,
} from "../../scripts/ubiquitiUtils";

const DEFAULT_INTERNET_SERIES_VISIBILITY = {
  download: true,
  upload: true,
  latency: true,
  packetLoss: true,
};

const DEFAULT_WIFI_SERIES_VISIBILITY = {
  activity: true,
  retries: true,
  clients: true,
};

const DEFAULT_AIRTIME_SERIES_VISIBILITY = {
  totalAirtime: true,
  multicast: true,
};

const DEFAULT_FLOWS_SERIES_VISIBILITY = {
  allowed: true,
  blocked: true,
};

const UBIQUITI_QUERY_OPTIONS = {
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 30000,
};

const keepPreviousUbiquitiData = (previousData) => previousData;

const UBIQUITI_GRAPH_QUERY_OPTIONS = {
  ...UBIQUITI_QUERY_OPTIONS,
  placeholderData: keepPreviousUbiquitiData,
};

const flattenUbiquitiObjects = (value, depth = 0) => {
  if (!value || depth > 4) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenUbiquitiObjects(item, depth + 1));
  }

  if (typeof value !== "object") {
    return [];
  }

  return [
    value,
    ...Object.values(value)
      .filter((item) => item && typeof item === "object")
      .flatMap((item) => flattenUbiquitiObjects(item, depth + 1)),
  ];
};

const normalizeUbiquitiKey = (key) => String(key || "").replace(/[\\_\-\s]/g, "").toLowerCase();

const getUbiquitiObjectValue = (source, key) => {
  if (!source || typeof source !== "object") {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(source, key)) {
    return source[key];
  }

  const normalizedKey = normalizeUbiquitiKey(key);
  const matchingKey = Object.keys(source).find((sourceKey) => normalizeUbiquitiKey(sourceKey) === normalizedKey);

  return matchingKey ? source[matchingKey] : undefined;
};

const pickUbiquitiValue = (sources, keys) => {
  for (const source of sources.flatMap((item) => flattenUbiquitiObjects(item))) {
    for (const key of keys) {
      const value = key.includes(".")
        ? key.split(".").reduce((currentValue, pathKey) => getUbiquitiObjectValue(currentValue, pathKey), source)
        : getUbiquitiObjectValue(source, key);

      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
  }

  return null;
};

const toUbiquitiDisplayNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  if (typeof value !== "string") {
    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? numericValue : null;
  }

  // Normalize common dash/minus characters and non-breaking spaces so negative dBm like
  // '−75 dBm' (unicode minus) are parsed correctly as -75.
  const normalizedInput = value
    .replace(/[\u2010-\u2015\u2212\u2012\u2013]/g, "-") // various dash/minus chars -> ascii hyphen
    .replace(/\u00A0/g, " ") // NBSP -> space
    .trim();

  const matchedValue = normalizedInput.match(/-?\d+(?:[\s.,]\d+)*/)?.[0];

  if (!matchedValue) {
    return null;
  }

  const compactValue = matchedValue.replace(/\s/g, "");
  const lastCommaIndex = compactValue.lastIndexOf(",");
  const lastDotIndex = compactValue.lastIndexOf(".");
  const decimalSeparator = lastCommaIndex > lastDotIndex ? "," : lastDotIndex > -1 ? "." : "";
  const normalizedValue = !decimalSeparator
    ? compactValue
    : compactValue
      .replace(decimalSeparator === "," ? /\./g : /,/g, "")
      .replace(decimalSeparator, ".");
  const numericValue = Number(normalizedValue);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatFrenchDecimal = (value, minimumFractionDigits = 1) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return null;
  }

  const plainValue = String(numericValue).includes("e")
    ? numericValue.toLocaleString("en-US", { maximumFractionDigits: 20, useGrouping: false })
    : String(numericValue);
  const [integerPart, fractionPart = ""] = plainValue.split(".");
  const sign = integerPart.startsWith("-") ? "-" : "";
  const absoluteInteger = integerPart.replace("-", "") || "0";
  const groupedInteger = Number(absoluteInteger).toLocaleString("fr-FR", { maximumFractionDigits: 0 });
  const visibleFraction = (fractionPart || "").padEnd(minimumFractionDigits, "0");

  return visibleFraction ? `${sign}${groupedInteger},${visibleFraction}` : `${sign}${groupedInteger}`;
};

const normalizeMetricTimestamp = (value) => {
  if (!value) {
    return 0;
  }

  const timestamp = typeof value === "number" && value < 1000000000000 ? value * 1000 : value;
  const time = new Date(timestamp).getTime();

  return Number.isNaN(time) ? 0 : time;
};

const formatInternetActivityAxisLabel = (value) => {
  const timestamp = normalizeMetricTimestamp(value);

  return timestamp ? new Date(timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "";
};

const formatInternetActivityTooltipLabel = (value) => {
  const timestamp = normalizeMetricTimestamp(value);

  return timestamp
    ? new Date(timestamp).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    })
    : "";
};

const getUbiquitiAxisLabelIndexes = (totalRows, labelCount = 7) => {
  if (totalRows <= 0) {
    return new Set();
  }

  if (totalRows <= labelCount) {
    return new Set(Array.from({ length: totalRows }, (_, index) => index));
  }

  return new Set(Array.from({ length: labelCount }, (_, index) => Math.round((index * (totalRows - 1)) / (labelCount - 1))));
};

const getInternetActivityPoints = (internetActivity) => {
  if (Array.isArray(internetActivity?.points)) {
    return internetActivity.points;
  }

  const activityRows = getUbiquitiDataArray(internetActivity);

  return activityRows.flatMap((row) => (Array.isArray(row?.points) ? row.points : []));
};

const buildInternetActivitySeries = (internetActivity) => {
  const points = getInternetActivityPoints(internetActivity)
    .filter((point) => point?.timestamp)
    .sort((first, second) => normalizeMetricTimestamp(first.timestamp) - normalizeMetricTimestamp(second.timestamp));
  const labelIndexes = getUbiquitiAxisLabelIndexes(points.length);
  const rows = points.map((point, index) => {
    const isLastPoint = index === points.length - 1;
    const isTickPoint = labelIndexes.has(index);
    const timestamp = point.timestamp;

    return {
      label: isLastPoint ? "Now" : isTickPoint ? formatInternetActivityAxisLabel(timestamp) : "",
      tooltipLabel: isLastPoint ? `${formatInternetActivityTooltipLabel(timestamp)} - Now` : formatInternetActivityTooltipLabel(timestamp),
      usesIspMetrics: true,
      avgDownload: getFirstNumberValue([point], ["avgDownloadMbps"]),
      avgUpload: getFirstNumberValue([point], ["avgUploadMbps"]),
      avgLatency: getFirstNumberValue([point], ["avgLatencyMs"]),
      minLatency: getFirstNumberValue([point], ["minLatencyMs"]),
      maxLatency: getFirstNumberValue([point], ["maxLatencyMs"]),
      packetLoss: getFirstNumberValue([point], ["packetLossPercent"]),
      connections: getFirstNumberValue([point], ["connections"]),
    };
  });

  return rows.filter((row) => (
    row.avgDownload !== null
    || row.avgUpload !== null
    || row.avgLatency !== null
    || row.maxLatency !== null
    || row.packetLoss !== null
    || row.connections !== null
  ));
};

const getUbiquitiEntityId = (source) => pickUbiquitiValue([source], ["id", "_id", "uuid", "mac", "macAddress", "deviceId", "clientId"]) || "";

const getFirstDisplayValue = (sources, keys) => pickUbiquitiValue(sources.filter(Boolean), keys);

const getFirstNumberValue = (sources, keys) => {
  const value = getFirstDisplayValue(sources, keys);
  const numericValue = toUbiquitiDisplayNumber(value);

  return numericValue !== null ? numericValue : null;
};

const formatPercentValue = (value, fallback = "-") => {
  const numericValue = toUbiquitiDisplayNumber(value);

  return numericValue !== null ? `${formatFrenchDecimal(Math.max(0, Math.min(100, numericValue)), 0)}%` : fallback;
};

const getBandKey = (source) => {
  const value = getFirstDisplayValue([source], [
    "band",
    "frequency",
    "frequencyBand",
    "wifiBand",
    "radio",
    "radioBand",
    "radioType",
    "radioName",
    "radio_name",
    "radio.band",
    "radio.type",
    "radio.name",
    "radioCode",
    "configuration.radio",
    "configuration.band",
  ]);
  const label = String(value || "").trim().toLowerCase();
  const compactLabel = label.replace(/[\s_\-]/g, "");
  const numericValue = Number(label.replace(",", ".").match(/\d+(?:\.\d+)?/)?.[0]);

  if (Number.isFinite(numericValue) && numericValue >= 2400) {
    if (numericValue >= 5925) {
      return "6";
    }

    if (numericValue >= 4900) {
      return "5";
    }

    if (numericValue >= 2400 && numericValue < 2500) {
      return "2.4";
    }
  }

  if (compactLabel === "6"
    || compactLabel === "6g"
    || compactLabel === "6ghz"
    || compactLabel === "6e"
    || compactLabel.includes("6ghz")
    || compactLabel.includes("6e")) {
    return "6";
  }

  if (compactLabel === "5"
    || compactLabel === "5g"
    || compactLabel === "5ghz"
    || compactLabel === "na"
    || compactLabel === "11a"
    || compactLabel === "11na"
    || compactLabel.includes("5ghz")) {
    return "5";
  }

  if (compactLabel === "2"
    || compactLabel === "2g"
    || compactLabel === "2ghz"
    || compactLabel === "ng"
    || compactLabel === "11g"
    || compactLabel === "11ng"
    || compactLabel.includes("2.4")
    || compactLabel.includes("24ghz")) {
    return "2.4";
  }

  if (compactLabel === "all" || compactLabel === "allradios") {
    return "all";
  }

  return "";
};

const getApSourceBundle = (ap) => [
  ap,
  ap?.raw,
].filter(Boolean);

const getApRadioBand = (sources) => getBandKey(sources.find((source) => getBandKey(source))) || "";

const getApTxRetry = (sources, fallbackRetry = null) => {
  const value = getFirstNumberValue(sources, [
    "txRetry",
    "txRetries",
    "txRetryRate",
    "txRetryPercentage",
    "radio.txRetry",
    "radio.txRetries",
    "statistics.txRetry",
    "statistics.txRetries",
    "wifi.txRetry",
    "wifi.txRetries",
  ]);

  return value !== null ? value : fallbackRetry;
};

const buildApRadioRows = (aps, activeFrequency, fallbackRetry) => aps
  .slice(0, 6)
  .map((ap, index) => {
    const sources = getApSourceBundle(ap);
    const band = getApRadioBand(sources);

    return {
      id: getUbiquitiEntityId(ap) || ap.name || `ap-${index}`,
      name: ap.name || ap.model || `AP${index + 1}`,
      band,
      clients: getFirstNumberValue(sources, ["clients", "numClients", "clientCount", "radio.clients"]),
      phyRate: getFirstNumberValue(sources, ["maxPhyRate", "phyRate", "radio.maxPhyRate", "statistics.maxPhyRate"]) ?? null,
      traffic: getFirstNumberValue(sources, ["traffic", "trafficBytes", "usageBytes", "statistics.traffic"]) ?? null,
      signal: getFirstNumberValue(sources, ["avgClientSignal", "clientSignal", "signal", "rssi", "radio.avgClientSignal", "statistics.avgClientSignal"]) ?? null,
      txRetry: getApTxRetry(sources, fallbackRetry),
    };
  })
  .filter((ap) => !activeFrequency || !ap.band || ap.band === activeFrequency);

const TX_RETRY_BUCKETS = [
  { label: "35%+", min: 35, className: "is-critical" },
  { label: "25%", min: 25, className: "is-warning" },
  { label: "20%", min: 20, className: "is-ok-soft" },
  { label: "15%", min: 15, className: "is-ok" },
  { label: "10%", min: 10, className: "is-ok-soft" },
  { label: "5%", min: 5, className: "is-ok" },
  { label: "0%", min: 0, className: "is-ok" },
];

const CONNECTIVITY_LABELS = ["Association", "Authentication", "DHCP", "DNS"];
const DENSITY_LOADING_MARKERS = ["47%", "51%", "56%", "64%"];

const getRetryBucketIndex = (value) => TX_RETRY_BUCKETS.findIndex((bucket) => value >= bucket.min);

const buildTxRetryBuckets = (apRows) => {
  const counts = TX_RETRY_BUCKETS.map((bucket) => ({ ...bucket, count: 0, aps: [], avgTxRetriesDisplay: "-" }));

  apRows.forEach((ap) => {
    if (!Number.isFinite(ap.txRetry)) {
      return;
    }

    const retryBucketIndex = getRetryBucketIndex(ap.txRetry);
    const bucketIndex = retryBucketIndex === -1 ? counts.length - 1 : retryBucketIndex;

    counts[bucketIndex].count += 1;
    counts[bucketIndex].aps.push(ap);
  });

  return counts.map((bucket) => {
    const retryValues = bucket.aps.map((ap) => ap.txRetry).filter(Number.isFinite);
    const avgTxRetries = retryValues.length
      ? retryValues.reduce((sum, value) => sum + value, 0) / retryValues.length
      : null;

    return {
      ...bucket,
      avgTxRetries,
      avgTxRetriesDisplay: avgTxRetries !== null ? `${formatFrenchDecimal(Math.round(avgTxRetries * 10) / 10, 1)}%` : "-",
      bandLabel: "Selected",
      totalApCount: apRows.length,
    };
  });
};

const getApiBandKey = (band) => getBandKey({
  band: band?.band || band?.label || band?.radio,
  frequency: band?.label,
  radio: band?.radio,
});

const DEFAULT_WIFI_RADIO_OPTIONS = [
  { value: "all", label: "All Radios", radio: "all" },
  { value: "2.4", label: "2.4 GHz", radio: "2.4" },
  { value: "5", label: "5 GHz", radio: "5" },
  { value: "6", label: "6 GHz", radio: "6" },
];

const DEFAULT_AIRTIME_RADIO_OPTIONS = [
  ...DEFAULT_WIFI_RADIO_OPTIONS,
];

const DEFAULT_AIRTIME_AP_OPTIONS = [
  { value: "all", label: "All APs", name: "All APs" },
];

const getWifiActivityRadioParam = (frequency) => {
  const radioParams = {
    all: "all",
    "2.4": "2.4",
    5: "5",
    6: "6",
  };

  return radioParams[frequency] || frequency || "all";
};

const getWifiActivityRadioOptions = (wifiActivity) => {
  const payload = wifiActivity?.data && typeof wifiActivity.data === "object" && !Array.isArray(wifiActivity.data)
    ? wifiActivity.data
    : wifiActivity;
  const availableRadios = Array.isArray(payload?.availableRadios) ? payload.availableRadios : [];
  const options = availableRadios
    .map((radio) => {
      const value = getApiBandKey(radio);

      return value
        ? {
          value,
          label: radio.label || (value === "all" ? "All Radios" : getUbiquitiFrequencyProfile(value).label) || value,
          radio: radio.radio || getWifiActivityRadioParam(value),
        }
        : null;
    })
    .filter(Boolean);

  return options.length ? options : DEFAULT_WIFI_RADIO_OPTIONS;
};

const getWrappedUbiquitiPayload = (response) => (
  response?.data && typeof response.data === "object" && !Array.isArray(response.data)
    ? response.data
    : response
);

const getAirtimeApOptions = (wifiAirtime) => {
  const payload = getWrappedUbiquitiPayload(wifiAirtime);
  const options = Array.isArray(payload?.apOptions) ? payload.apOptions : [];
  const normalizedOptions = options
    .map((ap) => {
      const value = ap.value || ap.id || ap.mac;

      return value
        ? {
          value,
          label: ap.label || ap.name || value,
          name: ap.name || ap.label || value,
        }
        : null;
    })
    .filter(Boolean);

  return normalizedOptions.length ? normalizedOptions : DEFAULT_AIRTIME_AP_OPTIONS;
};

const getAirtimeRadioOptions = (wifiAirtime) => {
  const payload = getWrappedUbiquitiPayload(wifiAirtime);
  const options = Array.isArray(payload?.availableRadios) ? payload.availableRadios : [];
  const normalizedOptions = options
    .map((radio) => {
      const value = getApiBandKey(radio);

      return value
        ? {
          value,
          label: radio.label || (value === "all" ? "All Radios" : getUbiquitiFrequencyProfile(value).label) || value,
          radio: value,
        }
        : null;
    })
    .filter(Boolean);

  return normalizedOptions.length ? normalizedOptions : DEFAULT_AIRTIME_RADIO_OPTIONS;
};

const getAirtimePointValue = (point, key) => {
  const numericValue = toUbiquitiDisplayNumber(point?.[key]);

  return numericValue !== null ? numericValue : null;
};

const buildUbiquitiAirtimeSeries = (wifiAirtime) => {
  const payload = getWrappedUbiquitiPayload(wifiAirtime);
  const points = (Array.isArray(payload?.points) ? payload.points : getUbiquitiDataArray(payload))
    .filter((point) => point?.timestamp)
    .sort((first, second) => normalizeMetricTimestamp(first.timestamp) - normalizeMetricTimestamp(second.timestamp));
  const labelIndexes = getUbiquitiAxisLabelIndexes(points.length);

  return points.map((point, index) => {
    const isLastPoint = index === points.length - 1;
    const isTickPoint = labelIndexes.has(index);
    const timestamp = point.timestamp;

    return {
      label: isLastPoint ? "Now" : isTickPoint ? formatInternetActivityAxisLabel(timestamp) : "",
      tooltipLabel: isLastPoint ? `${formatInternetActivityTooltipLabel(timestamp)} - Now` : formatInternetActivityTooltipLabel(timestamp),
      totalAirtime: getAirtimePointValue(point, "totalAirtimePercent"),
      totalAirtimeDisplay: point.totalAirtimeDisplay,
      externalInterference: getAirtimePointValue(point, "externalInterferencePercent"),
      externalInterferenceDisplay: point.externalInterferenceDisplay,
      internalInterference: getAirtimePointValue(point, "internalInterferencePercent"),
      internalInterferenceDisplay: point.internalInterferenceDisplay,
      multicast: getAirtimePointValue(point, "multicastPercent"),
      multicastDisplay: point.multicastDisplay,
      txMulticast: getAirtimePointValue(point, "txMulticastPercent"),
      txMulticastDisplay: point.txMulticastDisplay,
      rxMulticast: getAirtimePointValue(point, "rxMulticastPercent"),
      rxMulticastDisplay: point.rxMulticastDisplay,
      rxMulticastPackets: getAirtimePointValue(point, "rxMulticastPackets"),
      rxMulticastPacketsDisplay: point.rxMulticastPacketsDisplay,
      txMulticastPackets: getAirtimePointValue(point, "txMulticastPackets"),
      txMulticastPacketsDisplay: point.txMulticastPacketsDisplay,
      usesAirtime: true,
    };
  });
};

const buildUbiquitiFlowsSeries = (flowsActivity) => {
  const payload = getWrappedUbiquitiPayload(flowsActivity);
  const points = (Array.isArray(payload?.points) ? payload.points : getUbiquitiDataArray(payload))
    .filter((point) => point?.timestamp)
    .sort((first, second) => normalizeMetricTimestamp(first.timestamp) - normalizeMetricTimestamp(second.timestamp));
  const labelIndexes = getUbiquitiAxisLabelIndexes(points.length);

  return points.map((point, index) => {
    const isLastPoint = index === points.length - 1;
    const isTickPoint = labelIndexes.has(index);
    const timestamp = point.timestamp;

    return {
      label: isLastPoint ? "Now" : isTickPoint ? formatInternetActivityAxisLabel(timestamp) : "",
      tooltipLabel: isLastPoint ? `${formatInternetActivityTooltipLabel(timestamp)} - Now` : formatInternetActivityTooltipLabel(timestamp),
      allowed: toUbiquitiDisplayNumber(point.allowed),
      blocked: toUbiquitiDisplayNumber(point.blocked),
      allowedDisplay: point.allowedDisplay,
      blockedDisplay: point.blockedDisplay,
      usesFlows: true,
    };
  });
};

const formatFlowsTotal = (totals, key) => {
  const displayValue = totals?.[`${key}Display`];

  if (displayValue !== undefined && displayValue !== null && displayValue !== "") {
    return String(displayValue).replace(".", ",");
  }

  const numericValue = toUbiquitiDisplayNumber(totals?.[key]);

  return numericValue !== null ? formatFrenchDecimal(numericValue, 0) : "-";
};

const getUbiquitiFlowsTotals = (flowsActivity) => {
  const payload = getWrappedUbiquitiPayload(flowsActivity);
  const totals = payload?.totals || {};

  return {
    allowedDisplay: formatFlowsTotal(totals, "allowed"),
    blockedDisplay: formatFlowsTotal(totals, "blocked"),
  };
};

const getDashboardRadioGraph = (radioGraphs, radio) => {
  const payload = getWrappedUbiquitiPayload(radioGraphs);

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return payload || null;
  }

  return payload[radio] || payload[getWifiActivityRadioParam(radio)] || payload.all || null;
};

const getDashboardAirtimeGraph = (wifiAirtime, ap, radio) => {
  const payload = getWrappedUbiquitiPayload(wifiAirtime);

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return payload || null;
  }

  const apPayload = payload[ap] || payload.all;

  if (!apPayload || typeof apPayload !== "object" || Array.isArray(apPayload)) {
    return apPayload || null;
  }

  return apPayload[radio] || apPayload[getWifiActivityRadioParam(radio)] || apPayload.all || null;
};

const mergeApRadioTxRetriesBands = (apRadioTxRetries) => {
  const bands = Array.isArray(apRadioTxRetries?.bands) ? apRadioTxRetries.bands : [];
  const selectedBands = bands.filter((band) => band?.txRetries || band?.density);

  if (!selectedBands.length) {
    return null;
  }

  const bucketMap = selectedBands.reduce((accumulator, band) => {
    const buckets = Array.isArray(band?.txRetries?.buckets) ? band.txRetries.buckets : [];

    buckets.forEach((bucket) => {
      const label = bucket.label;

      if (!label) {
        return;
      }

      const currentBucket = accumulator.get(label) || {
        label,
        apCount: 0,
        weightedRetries: 0,
        weight: 0,
        level: bucket.level,
      };
      const apCount = toUbiquitiDisplayNumber(bucket.apCount) ?? 0;
      const avgTxRetries = toUbiquitiDisplayNumber(bucket.avgTxRetriesPercent);

      currentBucket.apCount += apCount;
      currentBucket.level ||= bucket.level;

      if (avgTxRetries !== null && apCount > 0) {
        currentBucket.weightedRetries += avgTxRetries * apCount;
        currentBucket.weight += apCount;
      }

      accumulator.set(label, currentBucket);
    });

    return accumulator;
  }, new Map());
  const densityPoints = selectedBands.flatMap((band) => (Array.isArray(band?.density?.points) ? band.density.points : []));
  const distributionMap = selectedBands.reduce((accumulator, band) => {
    const distribution = Array.isArray(band?.density?.distribution) ? band.density.distribution : [];

    distribution.forEach((row) => {
      const signal = row.signalDbm;
      const key = String(signal);
      const currentRow = accumulator.get(key) || {
        signalDbm: signal,
        signalDisplay: row.signalDisplay,
        apCount: 0,
        aps: [],
      };

      currentRow.apCount += toUbiquitiDisplayNumber(row.apCount) ?? 0;
      currentRow.aps.push(...(Array.isArray(row.aps) ? row.aps : []));
      accumulator.set(key, currentRow);
    });

    return accumulator;
  }, new Map());

  return {
    band: "all",
    radio: "all",
    label: "All Radios",
    txRetries: {
      totalApCount: selectedBands.reduce((sum, band) => sum + (toUbiquitiDisplayNumber(band?.txRetries?.totalApCount) ?? 0), 0),
      buckets: [...bucketMap.values()].map((bucket) => ({
        ...bucket,
        avgTxRetriesPercent: bucket.weight ? bucket.weightedRetries / bucket.weight : null,
        avgTxRetriesDisplay: bucket.weight ? `${formatFrenchDecimal(Math.round((bucket.weightedRetries / bucket.weight) * 10) / 10, 1)}%` : "-",
      })),
    },
    density: {
      points: densityPoints,
      distribution: [...distributionMap.values()],
    },
  };
};

const getSelectedApRadioTxRetriesBand = (apRadioTxRetries, activeFrequency) => {
  const bands = Array.isArray(apRadioTxRetries?.bands) ? apRadioTxRetries.bands : [];

  if (activeFrequency === "all") {
    return mergeApRadioTxRetriesBands(apRadioTxRetries);
  }

  return bands.find((band) => getApiBandKey(band) === activeFrequency) || null;
};

const getTxRetryLevelClassName = (level) => {
  const normalizedLevel = String(level || "").toLowerCase();

  if (normalizedLevel.includes("danger") || normalizedLevel.includes("critical")) {
    return "is-critical";
  }

  if (normalizedLevel.includes("warning") || normalizedLevel.includes("warn")) {
    return "is-warning";
  }

  if (normalizedLevel.includes("good") || normalizedLevel.includes("ok") || normalizedLevel.includes("success")) {
    return "is-ok";
  }

  return "is-ok-soft";
};

const buildApiTxRetryBuckets = (apRadioTxRetries, activeFrequency) => {
  const selectedBand = getSelectedApRadioTxRetriesBand(apRadioTxRetries, activeFrequency);

  if (!selectedBand?.txRetries) {
    return [];
  }

  const apiBuckets = Array.isArray(selectedBand.txRetries.buckets) ? selectedBand.txRetries.buckets : [];
  const scaleLabels = Array.isArray(apRadioTxRetries?.txRetryScaleLabels) && apRadioTxRetries.txRetryScaleLabels.length
    ? apRadioTxRetries.txRetryScaleLabels
    : TX_RETRY_BUCKETS.map((bucket) => bucket.label);

  return scaleLabels.map((label) => {
    const apiBucket = apiBuckets.find((bucket) => bucket.label === label) || {};
    const fallbackBucket = TX_RETRY_BUCKETS.find((bucket) => bucket.label === label) || TX_RETRY_BUCKETS.at(-1);

    return {
      ...fallbackBucket,
      label,
      count: apiBucket.apCount ?? 0,
      avgTxRetries: apiBucket.avgTxRetriesPercent ?? null,
      avgTxRetriesDisplay: apiBucket.avgTxRetriesDisplay || "-",
      bandLabel: selectedBand.label || label,
      className: getTxRetryLevelClassName(apiBucket.level) || fallbackBucket.className,
      totalApCount: selectedBand.txRetries.totalApCount ?? 0,
    };
  });
};

const getWifiActivityConnectivitySource = (wifiActivity, activeFrequency) => {
  const payload = wifiActivity?.data && typeof wifiActivity.data === "object" && !Array.isArray(wifiActivity.data)
    ? wifiActivity.data
    : wifiActivity;
  const latestWifiActivity = payload?.latest || getUbiquitiDataArray(payload).at(-1) || payload?.data?.at?.(-1) || payload?.data || payload;
  const wifiConnectivity = latestWifiActivity?.wifiConnectivity || payload?.wifiConnectivity || {};
  const bands = Array.isArray(wifiConnectivity.bands) ? wifiConnectivity.bands : [];
  const selectedBandConnectivity = bands.find((band) => getApiBandKey(band) === activeFrequency);

  return selectedBandConnectivity || wifiConnectivity || latestWifiActivity;
};

const getWifiConnectivityStepValue = (source, stepKey, legacyKeys) => {
  const step = source?.[stepKey] || (Array.isArray(source?.steps) ? source.steps.find((item) => item.key === stepKey) : null);

  return getFirstNumberValue([step, source], ["percent", "value", ...legacyKeys]);
};

const buildWifiConnectivityRows = (wifiActivity, activeFrequency) => {
  const source = getWifiActivityConnectivitySource(wifiActivity, activeFrequency);

  return [
    { key: "association", label: "Association", value: getWifiConnectivityStepValue(source, "association", ["association", "associationSuccess", "associationRate"]) },
    { key: "authentication", label: "Authentication", value: getWifiConnectivityStepValue(source, "authentication", ["authentication", "authenticationSuccess", "authSuccess", "authRate"]) },
    { key: "dhcp", label: "DHCP", value: getWifiConnectivityStepValue(source, "dhcp", ["dhcp", "dhcpSuccess", "dhcpRate"]) },
    { key: "dns", label: "DNS", value: getWifiConnectivityStepValue(source, "dns", ["dns", "dnsSuccess", "dnsRate"]) },
  ];
};

const getWifiConnectivitySuccess = (wifiActivity, activeFrequency) => {
  const source = getWifiActivityConnectivitySource(wifiActivity, activeFrequency);
  const success = getFirstNumberValue([source], ["successPercent", "success", "successRate", "successPercentage"]);

  return success !== null ? Math.round(success) : null;
};

const buildDensityMarkers = (accessPoints, apRows) => {
  const accessPointSources = getUbiquitiDataArray(accessPoints);
  const markerSources = accessPointSources.length ? accessPointSources : apRows;

  return markerSources
    .map((source, index) => {
      const signal = getFirstNumberValue([source], ["avgSignal", "signal", "rssi", "dbm", "avgClientSignal", "clientSignal"]) ?? apRows[index]?.signal;

      return {
        id: getUbiquitiEntityId(source) || apRows[index]?.id || `density-${index}`,
        name: getFirstDisplayValue([source], ["name", "displayName", "label"]) || apRows[index]?.name || `AP${index + 1}`,
        signal,
        count: getFirstNumberValue([source], ["count", "nearbyCount", "overlapCount"]) ?? null,
      };
    })
    .filter((marker) => Number.isFinite(marker.signal))
    .slice(0, 8);
};

const getDensityPointId = (point) => point?.apId || point?.id || point?.mac || point?.name || "";

const buildApiDensityMarkers = (apRadioTxRetries, activeFrequency) => {
  const selectedBand = getSelectedApRadioTxRetriesBand(apRadioTxRetries, activeFrequency);
  const density = selectedBand?.density;
  const densityPoints = Array.isArray(density?.points) ? density.points : [];
  const distribution = Array.isArray(density?.distribution) ? density.distribution : [];

  if (!densityPoints.length && !distribution.length) {
    return [];
  }

  // Liste à plat de tous les APs individuels, peu importe comment l'API les avait groupés.
  const flatAps = densityPoints.length
    ? densityPoints
    : distribution.flatMap((row) => (Array.isArray(row.aps) ? row.aps : []));

  // On regroupe nous-mêmes par la valeur ARRONDIE réellement affichée
  // (celle utilisée dans "Avg. Client Signal"), pas par le bucket de l'API.
  const groups = new Map();

  flatAps.forEach((ap, index) => {
    const resolvedSignalSource = ap.weakestClientSignalDisplay || ap.signalDisplay || ap.signalDbm;
    const signal = toUbiquitiDisplayNumber(resolvedSignalSource);

    if (!Number.isFinite(signal)) {
      return;
    }

    const groupKey = Math.round(signal); // correspond au "-75 dBm" affiché

    const group = groups.get(groupKey) || {
      id: `${selectedBand.band || activeFrequency}-${groupKey}-${index}`,
      signal: groupKey,
      signalDisplay: ap.weakestClientSignalDisplay || ap.signalDisplay || `${groupKey} dBm`,
      aps: [],
    };

    group.aps.push(ap);
    groups.set(groupKey, group);
  });

  return [...groups.values()]
    .map((group) => ({
      id: group.id,
      name: group.aps[0]?.name || "AP",
      signal: group.signal,
      signalDisplay: group.signalDisplay,
      count: group.aps.length,
      aps: group.aps,
    }))
    .slice(0, 12);
};

const getDensityMarkerTone = (signal) => {
  if (!Number.isFinite(signal)) {
    return "is-good";
  }

  if (signal <= -82) {
    return "is-critical";
  }

  if (signal <= -76) {
    return "is-warning";
  }

  return "is-good";
};

const getDensityStatusDetails = (apRadioTxRetries, activeFrequency, densityMarkers) => {
  const signals = densityMarkers.map((marker) => marker.signal).filter(Number.isFinite);

  if (!signals.length) {
    const selectedBand = getSelectedApRadioTxRetriesBand(apRadioTxRetries, activeFrequency);
    const apiDensity = selectedBand?.density;
    const normalizedStatus = String(apiDensity?.status || "").toLowerCase();

    if (apiDensity?.display) {
      return {
        className: normalizedStatus.includes("bad") || normalizedStatus.includes("poor")
          ? "is-critical"
          : normalizedStatus.includes("warn") || normalizedStatus.includes("improvement")
            ? "is-warning"
            : "is-good",
        label: apiDensity.display,
      };
    }

    return { className: "is-neutral", label: "AP Density data unavailable" };
  }

  const weakestSignal = Math.min(...signals);

  if (weakestSignal <= -82) {
    return { className: "is-critical", label: "AP Density Requires Immediate Attention" };
  }

  if (weakestSignal <= -76) {
    return { className: "is-warning", label: "AP Deployment Density May Need Improvement" };
  }

  return { className: "is-good", label: "AP Density is Good" };
};

const getDensityScale = (apRadioTxRetries, activeFrequency) => {
  const selectedBand = getSelectedApRadioTxRetriesBand(apRadioTxRetries, activeFrequency);
  const scale = selectedBand?.density?.scale || apRadioTxRetries?.densityScale || {};
  const minDbm = toUbiquitiDisplayNumber(scale.minDbm) ?? -90;
  const maxDbm = toUbiquitiDisplayNumber(scale.maxDbm) ?? -30;

  return minDbm < maxDbm ? { minDbm, maxDbm } : { minDbm: -90, maxDbm: -30 };
};

const UbiquitiWifiDiagnostics = ({
  activeFrequency,
  apRows,
  txRetryBuckets,
  connectivityRows,
  connectivitySuccess,
  densityMarkers,
  densityScale,
  densityStatus,
  frequencyProfile,
  isLoading,
  setActiveFrequency,
}) => {
  const [densityTooltipIndexes, setDensityTooltipIndexes] = useState({});
  const retryValues = apRows
    .map((ap) => ap.txRetry)
    .filter(Number.isFinite);
  const avgRetry = retryValues.length
    ? retryValues.reduce((sum, value) => sum + value, 0) / retryValues.length
    : null;
  const apiRetryAverageBuckets = txRetryBuckets.filter((bucket) => bucket.count > 0 && Number.isFinite(bucket.avgTxRetries));
  const displayedAvgRetry = apiRetryAverageBuckets.length
    ? apiRetryAverageBuckets.reduce((sum, bucket) => sum + (bucket.avgTxRetries * bucket.count), 0)
    / apiRetryAverageBuckets.reduce((sum, bucket) => sum + bucket.count, 0)
    : avgRetry;
  const success = connectivitySuccess ?? null;
  const densityTicks = Array.from({ length: Math.floor((densityScale.maxDbm - densityScale.minDbm) / 5) + 1 }, (_, index) => densityScale.minDbm + (index * 5));
  const densityRange = densityScale.maxDbm - densityScale.minDbm;
  const retryBarBuckets = txRetryBuckets.slice(0, 6);
  const retryScaleLabels = txRetryBuckets.map((bucket) => bucket.label);
  const visibleRetryScaleLabels = retryScaleLabels.length ? retryScaleLabels : TX_RETRY_BUCKETS.map((bucket) => bucket.label);

  const setDensityTooltipIndex = (event, marker, direction) => {
    event.preventDefault();
    event.stopPropagation();

    const totalAps = marker.aps?.length || 1;

    setDensityTooltipIndexes((currentIndexes) => ({
      ...currentIndexes,
      [marker.id]: (currentIndexes[marker.id] ?? 0) + direction < 0
        ? totalAps - 1
        : ((currentIndexes[marker.id] ?? 0) + direction) % totalAps,
    }));
  };

  if (isLoading) {
    return (
      <article className="ubiquiti-wifi-diagnostics is-loading" aria-busy="true">
        <div className="ubiquiti-radio-diagnostics">
          <div className="ubiquiti-frequency-tabs is-grid">
            {["2.4", "5", "6"].map((band) => (
              <button
                className={activeFrequency === band && !isLoading ? "is-active" : ""}
                type="button"
                key={band}
                onClick={() => setActiveFrequency(band)}
              >
                {band === "2.4" ? "2.4 GHz" : `${band} GHz`}
              </button>
            ))}
          </div>
          <section className="ubiquiti-retry-panel">
            <div className="ubiquiti-panel-heading ubiquiti-diagnostic-heading">
              <div className="ubiquiti-diagnostic-heading-main">
                AP Radio TX Retries
              </div>
            </div>
            <div className="ubiquiti-retry-bars is-loading">
              {TX_RETRY_BUCKETS.slice(0, 6).map((bucket, bucketIndex) => (
                <div className="ubiquiti-retry-bucket" key={bucket.label}>
                  <span
                    className="ubiquiti-diagnostics-loading-bar"
                    style={{ "--loading-delay": `${bucketIndex * 90}ms` }}
                  ></span>
                </div>
              ))}
              <div className="ubiquiti-retry-scale" aria-hidden="true">
                {visibleRetryScaleLabels.map((label, labelIndex) => (
                  <small
                    style={{ left: `${visibleRetryScaleLabels.length > 1 ? (labelIndex / (visibleRetryScaleLabels.length - 1)) * 100 : 0}%` }}
                    key={label}
                  >
                    {label}
                  </small>
                ))}
              </div>
            </div>
          </section>
          <section className="ubiquiti-connectivity-panel">
            <div className="ubiquiti-panel-heading ubiquiti-diagnostic-heading">
              <div className="ubiquiti-diagnostic-heading-main">
                WiFi Connectivity
                <span>Success <em className="ubiquiti-diagnostics-success-loading"></em></span>
              </div>
            </div>
            <div className="ubiquiti-connectivity-bars">
              {CONNECTIVITY_LABELS.map((label, labelIndex) => (
                <div className="ubiquiti-connectivity-row is-loading" key={label}>
                  <span style={{ "--loading-delay": `${labelIndex * 110}ms` }}>{label}</span>
                  <small style={{ "--loading-delay": `${labelIndex * 110}ms` }}></small>
                </div>
              ))}
            </div>
          </section>
        </div>
        <div className="ubiquiti-density-panel is-loading">
          <div className="ubiquiti-density-title is-loading">
            <span>
              <i aria-hidden="true"></i>
              <b aria-hidden="true"></b>
            </span>
          </div>
          <div className="ubiquiti-density-band-skeleton" aria-hidden="true">
            <span></span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="ubiquiti-wifi-diagnostics">
      <div className="ubiquiti-radio-diagnostics">
        <div className="ubiquiti-frequency-tabs is-grid">
          {["2.4", "5", "6"].map((band) => (
            <button
              className={activeFrequency === band ? "is-active" : ""}
              type="button"
              key={band}
              onClick={() => setActiveFrequency(band)}
            >
              {band === "2.4" ? "2.4 GHz" : `${band} GHz`}
            </button>
          ))}
        </div>
        <section className="ubiquiti-retry-panel">
          <div className="ubiquiti-panel-heading ubiquiti-diagnostic-heading">
            <div className="ubiquiti-diagnostic-heading-main">
              AP Radio TX Retries
            </div>
          </div>
          <div className="ubiquiti-retry-bars">
            {retryBarBuckets.map((bucket) => (
              <div className="ubiquiti-retry-bucket" key={bucket.label} tabIndex={0}>
                <span className={`${bucket.className} ${bucket.count ? "" : "is-inactive"}`.trim()}>{bucket.count || ""}</span>
                <div className="ubiquiti-retry-tooltip" role="tooltip">
                  Avg. TX Retries
                  <div>
                    <span>Band</span>
                    <span>APs</span>
                    <span>TX Retries</span>
                  </div>
                  <div>
                    <b>{bucket.bandLabel || frequencyProfile.label}</b>
                    <b>{bucket.count || 0}</b>
                    <b>{bucket.avgTxRetriesDisplay || "-"}</b>
                  </div>
                </div>
              </div>
            ))}
            <div className="ubiquiti-retry-scale" aria-hidden="true">
              {retryScaleLabels.map((label, labelIndex) => (
                <small
                  style={{ left: `${retryScaleLabels.length > 1 ? (labelIndex / (retryScaleLabels.length - 1)) * 100 : 0}%` }}
                  key={label}
                >
                  {label}
                </small>
              ))}
            </div>
          </div>
        </section>
        <section className="ubiquiti-connectivity-panel">
          <div className="ubiquiti-panel-heading ubiquiti-diagnostic-heading">
            <div className="ubiquiti-diagnostic-heading-main">
              WiFi Connectivity
              <span>Success <em>{success !== null ? `${success}%` : "-"}</em></span>
            </div>
          </div>
          <div className="ubiquiti-connectivity-bars">
            {connectivityRows.map((row) => {
              const width = Number.isFinite(row.value) ? Math.max(0, Math.min(100, row.value)) : 0;

              return (
                <div className="ubiquiti-connectivity-row" key={row.label}>
                  <span style={{ width: `${width}%` }}>{row.label}</span>
                  <small>{Number.isFinite(row.value) ? formatPercentValue(row.value) : "-"}</small>
                </div>
              );
            })}
          </div>
        </section>
      </div>
      <div className="ubiquiti-density-panel">
        <div className={`ubiquiti-density-title ${densityStatus.className}`}>
          <span>
            {densityStatus.className === "is-good" ? <FaCheckCircle /> : <FaExclamationTriangle />}
            {densityStatus.label}
          </span>
        </div>
        <div className="ubiquiti-density-scale">
          {densityTicks.map((tick) => (
            <small style={{ left: `${((tick - densityScale.minDbm) / densityRange) * 100}%` }} key={tick}>{tick}</small>
          ))}
          {densityMarkers.map((marker) => {
            const tooltipAps = marker.aps?.length ? marker.aps : [marker];
            const activeTooltipIndex = Math.min(densityTooltipIndexes[marker.id] ?? 0, tooltipAps.length - 1);
            const activeAp = tooltipAps[activeTooltipIndex] || marker;
            const signalDisplay = activeAp.weakestClientSignalDisplay || activeAp.signalDisplay || marker.signalDisplay || `${marker.signal} dBm`;
            const markerLeft = Math.max(0, Math.min(100, ((marker.signal - densityScale.minDbm) / densityRange) * 100));
            const markerTone = getDensityMarkerTone(marker.signal);

            return (
              <div
                className={`ubiquiti-density-marker ${markerTone}`}
                style={{ left: `${markerLeft}%` }}
                key={marker.id}
                role="button"
                tabIndex={0}
                aria-label={`${activeAp.name || marker.name} - ${signalDisplay}`}
              >
                <span aria-hidden="true"></span>
                {(marker.count || tooltipAps.length) > 1 ? <em>{marker.count || tooltipAps.length}</em> : null}
                <div className="ubiquiti-density-tooltip" role="tooltip">
                  <div className="ubiquiti-density-tooltip-title">
                    <i aria-hidden="true"></i>
                    {activeAp.name || marker.name}
                  </div>
                  <dl>
                    <div>
                      <dt>% of All Traffic</dt>
                      <dd>{activeAp.trafficDisplay || formatPercentValue(activeAp.trafficPercent)}</dd>
                    </div>
                    <div>
                      <dt>Max PHY Rate</dt>
                      <dd>{activeAp.maxPhyRateDisplay || (Number.isFinite(activeAp.maxPhyRateMbps ?? activeAp.phyRate) ? `${formatFrenchDecimal(activeAp.maxPhyRateMbps ?? activeAp.phyRate, 0)} Mbps` : "-")}</dd>
                    </div>
                    <div>
                      <dt>{frequencyProfile.label} Clients</dt>
                      <dd>{activeAp.clientsDisplay ?? activeAp.clientsCount ?? activeAp.clients ?? "-"}</dd>
                    </div>
                    <div>
                      <dt>Avg. Client Signal</dt>
                      <dd className={`is-signal ${markerTone}`}>{signalDisplay}</dd>
                    </div>
                    <div>
                      <dt>TX Retries</dt>
                      <dd>{activeAp.txRetriesDisplay || formatPercentValue(activeAp.txRetriesPercent ?? activeAp.txRetry)}</dd>
                    </div>
                  </dl>
                  {tooltipAps.length > 1 ? (
                    <div className="ubiquiti-density-tooltip-footer">
                      <button type="button" aria-label="Previous AP" onClick={(event) => setDensityTooltipIndex(event, marker, -1)}>
                        <FaChevronLeft />
                      </button>
                      <span>{activeTooltipIndex + 1} of {tooltipAps.length}</span>
                      <button type="button" aria-label="Next AP" onClick={(event) => setDensityTooltipIndex(event, marker, 1)}>
                        <FaChevronRight />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </article>
  );
};

export default function Ubiquiti() {
  const { apiUrl } = useContext(AuthContext);
  const { theme } = useTheme();
  const [activeView, setActiveView] = useState("internet");
  const [activeWifiChartMode, setActiveWifiChartMode] = useState("activity");
  const [activeWifiFrequency, setActiveWifiFrequency] = useState("all");
  const [activeApRadioFrequency, setActiveApRadioFrequency] = useState("5");
  const [activeAirtimeAp, setActiveAirtimeAp] = useState("all");
  const [activeAirtimeRadio, setActiveAirtimeRadio] = useState("all");
  const [activeWan, setActiveWan] = useState("all-wans");
  const [visibleInternetSeries, setVisibleInternetSeries] = useState(DEFAULT_INTERNET_SERIES_VISIBILITY);
  const [visibleWifiSeries, setVisibleWifiSeries] = useState(DEFAULT_WIFI_SERIES_VISIBILITY);
  const [visibleAirtimeSeries, setVisibleAirtimeSeries] = useState(DEFAULT_AIRTIME_SERIES_VISIBILITY);
  const [visibleFlowsSeries, setVisibleFlowsSeries] = useState(DEFAULT_FLOWS_SERIES_VISIBILITY);
  const [cachedAirtimeApOptions, setCachedAirtimeApOptions] = useState(DEFAULT_AIRTIME_AP_OPTIONS);
  const [cachedAirtimeRadioOptions, setCachedAirtimeRadioOptions] = useState(DEFAULT_AIRTIME_RADIO_OPTIONS);
  const [gatewaySummaryRefreshKey, setGatewaySummaryRefreshKey] = useState(0);
  const [ispPanelRefreshKey, setIspPanelRefreshKey] = useState(0);
  const [mostCommonDevicesRefreshKey, setMostCommonDevicesRefreshKey] = useState(0);
  const [topOverviewRefreshKey, setTopOverviewRefreshKey] = useState(0);
  const [wifiSpeedsRefreshKey, setWifiSpeedsRefreshKey] = useState(0);
  const [gatewaySummaryQueryState, setGatewaySummaryQueryState] = useState({});
  const [internetSummaryQueryState, setInternetSummaryQueryState] = useState({});
  const [wifiDefaultSpeedsQueryState, setWifiDefaultSpeedsQueryState] = useState({});
  const [topOverviewQueryState, setTopOverviewQueryState] = useState({});
  const [mostCommonDevicesQueryState, setMostCommonDevicesQueryState] = useState({});
  const sitesQuery = useQuery({
    ...UBIQUITI_QUERY_OPTIONS,
    queryKey: ["ubiquiti-sites", apiUrl],
    queryFn: () => getUbiquitiSites(apiUrl),
    enabled: !!apiUrl,
  });
  const { t } = useTranslation();

  const sites = getUbiquitiDataArray(sitesQuery.data);
  const site = sites[0] || {};
  const networkSiteId = site?.siteId || "";
  const canLoadCleanPanelData = !!apiUrl && !!networkSiteId;

  const dashboardGraphsQuery = useQuery({
    ...UBIQUITI_GRAPH_QUERY_OPTIONS,
    queryKey: ["ubiquiti-dashboard-graphs", networkSiteId],
    queryFn: () => getUbiquitiDashboardGraphs(apiUrl, networkSiteId),
    enabled: canLoadCleanPanelData,
  });

  const canLoadSecondaryUbiquitiData = canLoadCleanPanelData
    && (dashboardGraphsQuery.isFetched || dashboardGraphsQuery.isError);

  const isLoading = sitesQuery.isLoading;
  const isError = sitesQuery.isError || (!networkSiteId && !sitesQuery.isLoading);
  const topApCandidates = [];
  const cleanPanelQueryStates = [
    gatewaySummaryQueryState,
    internetSummaryQueryState,
    wifiDefaultSpeedsQueryState,
    topOverviewQueryState,
    mostCommonDevicesQueryState,
  ];
  const areCleanPanelQueriesReady = cleanPanelQueryStates.every((queryState) => (
    queryState.siteId === networkSiteId
    && (queryState.isFetched || queryState.isError)
    && !queryState.isFetching
  ));
  const isCleanPanelFetching = cleanPanelQueryStates.some((queryState) => (
    queryState.siteId === networkSiteId && queryState.isFetching
  ));
  const canLoadInternetLivePoint = canLoadCleanPanelData
    && (dashboardGraphsQuery.isFetched || dashboardGraphsQuery.isError);
  const consoleName = site?.meta?.desc || site?.name || "Ubiquiti";
  const wanRows = getUbiquitiWanRows(site);
  const currentWan = wanRows.some((wan) => wan.name === activeWan) ? activeWan : "all-wans";
  const dashboardGraphsPayload = getWrappedUbiquitiPayload(dashboardGraphsQuery.data) || {};
  const internetActivityData = dashboardGraphsPayload.internetActivity;
  const flowsActivityData = dashboardGraphsPayload.flowsActivity;
  const wifiActivityGraphs = dashboardGraphsPayload.wifiActivity;
  const wifiAirtimeGraphs = dashboardGraphsPayload.wifiAirtime;
  const wifiActivityData = getDashboardRadioGraph(wifiActivityGraphs, activeWifiFrequency);
  const apRadioWifiActivityData = getDashboardRadioGraph(wifiActivityGraphs, activeApRadioFrequency);
  const wifiAirtimeData = getDashboardAirtimeGraph(wifiAirtimeGraphs, activeAirtimeAp, activeAirtimeRadio);
  const apRadioTxRetriesData = dashboardGraphsPayload.apRadioTxRetries;
  const internetActivitySeries = useMemo(
    () => buildInternetActivitySeries(internetActivityData),
    [internetActivityData],
  );
  const internetSeries = internetActivitySeries;
  const latestInternetMetricRow = internetSeries.at(-1) || null;
  const airtimePayload = getWrappedUbiquitiPayload(wifiAirtimeGraphs);
  const hasAirtimeApOptionSource = Array.isArray(airtimePayload?.apOptions);
  const hasAirtimeRadioOptionSource = Array.isArray(airtimePayload?.availableRadios);
  const wifiFrequencyOptions = useMemo(
    () => getWifiActivityRadioOptions(wifiActivityGraphs?.all || wifiActivityData || wifiActivityGraphs),
    [wifiActivityData, wifiActivityGraphs],
  );
  const currentAirtimeApOptions = useMemo(
    () => getAirtimeApOptions(wifiAirtimeGraphs),
    [wifiAirtimeGraphs],
  );
  const currentAirtimeRadioOptions = useMemo(
    () => getAirtimeRadioOptions(wifiAirtimeGraphs),
    [wifiAirtimeGraphs],
  );
  const airtimeApOptions = hasAirtimeApOptionSource ? currentAirtimeApOptions : cachedAirtimeApOptions;
  const airtimeRadioOptions = hasAirtimeRadioOptionSource ? currentAirtimeRadioOptions : cachedAirtimeRadioOptions;
  const wifiSeries = useMemo(
    () => buildUbiquitiWifiSeries(site, activeWifiFrequency, [], wifiActivityData),
    [activeWifiFrequency, site, wifiActivityData],
  );
  const airtimeSeries = useMemo(
    () => buildUbiquitiAirtimeSeries(wifiAirtimeData),
    [wifiAirtimeData],
  );
  const flowsSeries = useMemo(
    () => buildUbiquitiFlowsSeries(flowsActivityData),
    [flowsActivityData],
  );
  const flowsTotals = useMemo(
    () => getUbiquitiFlowsTotals(flowsActivityData),
    [flowsActivityData],
  );
  const activeChartMode = activeView === "wifi" && activeWifiChartMode === "airtime" ? "airtime" : activeView;
  const chartRows = activeView === "wifi"
    ? activeWifiChartMode === "airtime" ? airtimeSeries : wifiSeries
    : activeView === "flows" ? flowsSeries : internetSeries;
  const activeGraphVisibleSeries = activeChartMode === "airtime"
    ? visibleAirtimeSeries
    : activeChartMode === "flows" ? visibleFlowsSeries : activeView === "wifi" ? visibleWifiSeries : visibleInternetSeries;
  const isChartFetching = dashboardGraphsQuery.isFetching;
  const isChartLoading = isChartFetching && chartRows.length === 0;
  const isChartUpdating = isChartFetching && chartRows.length > 0;
  const usesIspInternetMetrics = internetSeries.some((row) => row.usesIspMetrics);
  const frequencyProfile = getUbiquitiFrequencyProfile(activeApRadioFrequency);
  const healthScore = getUbiquitiHealthScore(site, []);
  const apRadioRows = buildApRadioRows(
    topApCandidates,
    activeApRadioFrequency,
    toUbiquitiDisplayNumber(site?.statistics?.percentages?.txRetry),
  );
  const displayedApRadioRows = apRadioRows.length ? apRadioRows : buildApRadioRows(
    topApCandidates,
    "",
    toUbiquitiDisplayNumber(site?.statistics?.percentages?.txRetry),
  );
  const apiTxRetryBuckets = buildApiTxRetryBuckets(apRadioTxRetriesData, activeApRadioFrequency);
  const txRetryBuckets = apiTxRetryBuckets.length ? apiTxRetryBuckets : buildTxRetryBuckets(displayedApRadioRows);
  const connectivityRows = buildWifiConnectivityRows(apRadioWifiActivityData, activeApRadioFrequency);
  const connectivitySuccess = getWifiConnectivitySuccess(apRadioWifiActivityData, activeApRadioFrequency);
  const chartConnectivityRows = buildWifiConnectivityRows(wifiActivityData, activeWifiFrequency);
  const wifiConnectivityValues = chartConnectivityRows
    .map((row) => row.value)
    .filter(Number.isFinite);
  const wifiConnectivityScore = wifiConnectivityValues.length
    ? Math.round(wifiConnectivityValues.reduce((sum, value) => sum + value, 0) / wifiConnectivityValues.length)
    : null;
  const apiDensityMarkers = buildApiDensityMarkers(apRadioTxRetriesData, activeApRadioFrequency);
  const densityMarkers = apiDensityMarkers.length ? apiDensityMarkers : buildDensityMarkers([], displayedApRadioRows);
  const densityScale = getDensityScale(apRadioTxRetriesData, activeApRadioFrequency);
  const densityStatus = getDensityStatusDetails(apRadioTxRetriesData, activeApRadioFrequency, densityMarkers);
  const isDiagnosticsLoading = !dashboardGraphsQuery.data && (dashboardGraphsQuery.isLoading || dashboardGraphsQuery.isFetching);
  const showToolbarSelector = activeView === "wifi" || (activeView === "internet" && wanRows.length > 1);
  const wifiLeftAxisLabel = visibleWifiSeries.retries
    ? visibleWifiSeries.clients ? `${t("clients unit")} / %` : "%"
    : t("clients unit");
  const isRefreshing = sitesQuery.isFetching
    || dashboardGraphsQuery.isFetching
    || isCleanPanelFetching;

  useEffect(() => {
    if (activeView !== "wifi" || !wifiFrequencyOptions.length) {
      return;
    }

    if (!wifiFrequencyOptions.some((option) => option.value === activeWifiFrequency)) {
      setActiveWifiFrequency(wifiFrequencyOptions[0].value);
    }
  }, [activeWifiFrequency, activeView, wifiFrequencyOptions]);

  useEffect(() => {
    if (hasAirtimeApOptionSource) {
      setCachedAirtimeApOptions(currentAirtimeApOptions);
    }

    if (hasAirtimeRadioOptionSource) {
      setCachedAirtimeRadioOptions(currentAirtimeRadioOptions);
    }
  }, [currentAirtimeApOptions, currentAirtimeRadioOptions, hasAirtimeApOptionSource, hasAirtimeRadioOptionSource]);

  useEffect(() => {
    if (activeView !== "wifi" || activeWifiChartMode !== "airtime") {
      return;
    }

    if (hasAirtimeApOptionSource && airtimeApOptions.length && !airtimeApOptions.some((option) => option.value === activeAirtimeAp)) {
      setActiveAirtimeAp(airtimeApOptions[0].value);
    }

    if (hasAirtimeRadioOptionSource && airtimeRadioOptions.length && !airtimeRadioOptions.some((option) => option.value === activeAirtimeRadio)) {
      setActiveAirtimeRadio(airtimeRadioOptions[0].value);
    }
  }, [
    activeAirtimeAp,
    activeAirtimeRadio,
    activeView,
    activeWifiChartMode,
    airtimeApOptions,
    airtimeRadioOptions,
    hasAirtimeApOptionSource,
    hasAirtimeRadioOptionSource,
  ]);

  useEffect(() => {
    if (activeView !== "internet") {
      return;
    }

    const mappedRows = internetSeries.map((row) => ({
      label: row.label,
      tooltipLabel: row.tooltipLabel,
      avgDownload: row.avgDownload,
      avgUpload: row.avgUpload,
      avgLatency: row.avgLatency,
      maxLatency: row.maxLatency,
      packetLoss: row.packetLoss,
      maxDownload: row.maxDownload,
      maxUpload: row.maxUpload,
      minLatency: row.minLatency,
      usesIspMetrics: row.usesIspMetrics,
    }));
    const debugState = {
      apiCall: `/ubiquiti/sites/${networkSiteId}/dashboard-graphs`,
      selectedIds: {
        siteId: networkSiteId,
      },
      pageApis: {
        sites: "/ubiquiti/sites",
        gatewaySummary: `/ubiquiti/sites/${networkSiteId}/gateway-summary`,
        dashboardGraphs: `/ubiquiti/sites/${networkSiteId}/dashboard-graphs`,
        internetSummary: `/ubiquiti/sites/${networkSiteId}/internet-summary`,
        internetLivePoint: `/ubiquiti/sites/${networkSiteId}/internet-live-point`,
        wifiDefaultSpeeds: `/ubiquiti/sites/${networkSiteId}/wifi-default-speeds`,
        topOverview: `/ubiquiti/sites/${networkSiteId}/top-overview`,
        mostCommonDevices: `/ubiquiti/sites/${networkSiteId}/most-common-devices`,
      },
      queryStatus: {
        isLoading: dashboardGraphsQuery.isLoading,
        isFetching: dashboardGraphsQuery.isFetching,
        isError: dashboardGraphsQuery.isError,
        error: dashboardGraphsQuery.error,
      },
      rawResponse: dashboardGraphsQuery.data,
      mappedRows,
    };

    window.__ubiquitiInternetDebug = debugState;
  }, [
    activeView,
    dashboardGraphsQuery.data,
    dashboardGraphsQuery.error,
    dashboardGraphsQuery.isError,
    dashboardGraphsQuery.isFetching,
    dashboardGraphsQuery.isLoading,
    internetSeries,
    networkSiteId,
  ]);

  const handleRefresh = async () => {
    if (networkSiteId) {
      const refetches = [
        dashboardGraphsQuery.refetch(),
        sitesQuery.refetch(),
      ];
      setGatewaySummaryRefreshKey((currentRefreshKey) => currentRefreshKey + 1);
      setIspPanelRefreshKey((currentRefreshKey) => currentRefreshKey + 1);
      setMostCommonDevicesRefreshKey((currentRefreshKey) => currentRefreshKey + 1);
      setTopOverviewRefreshKey((currentRefreshKey) => currentRefreshKey + 1);
      setWifiSpeedsRefreshKey((currentRefreshKey) => currentRefreshKey + 1);
      await Promise.allSettled(refetches);

      return;
    }

    await sitesQuery.refetch();
  };

  const toggleInternetSeries = (seriesKey) => {
    setVisibleInternetSeries((currentVisibility) => ({
      ...currentVisibility,
      [seriesKey]: !currentVisibility[seriesKey],
    }));
  };

  const toggleInternetActivity = () => {
    setVisibleInternetSeries((currentVisibility) => {
      const isVisible = currentVisibility.download || currentVisibility.upload;

      return {
        ...currentVisibility,
        download: !isVisible,
        upload: !isVisible,
      };
    });
  };

  const toggleWifiSeries = (seriesKey) => {
    setVisibleWifiSeries((currentVisibility) => ({
      ...currentVisibility,
      [seriesKey]: !currentVisibility[seriesKey],
    }));
  };

  const toggleAirtimeSeries = (seriesKey) => {
    setVisibleAirtimeSeries((currentVisibility) => ({
      ...currentVisibility,
      [seriesKey]: !currentVisibility[seriesKey],
    }));
  };

  const toggleFlowsSeries = (seriesKey) => {
    setVisibleFlowsSeries((currentVisibility) => ({
      ...currentVisibility,
      [seriesKey]: !currentVisibility[seriesKey],
    }));
  };

  return (
    <div className="ecp-container">
      <div className="ecp-container-content">
        {isLoading ? (
          <Loader />
        ) : isError || !sites.length ? (
          <div className="ecp-container-content-error">
            <div className="error-container">
              <img src="../assets/ecp_error.svg" alt={t("Error icon")} />
              <div className="error-container-text">
                <h1 className="page-title">{t("No Ubiquiti data available")}</h1>
                <p className="page-link_home">
                  <Link to="/">{t("Back to homepage")}</Link>
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="ecp-container-content-header">
              <div className="ecp-container-content-header-title">
                <div className="page-title page-logo">
                  <img src="/assets/ubiquiti_logo.svg" alt={t("Ubiquiti logo")} />
                  <h1 className="page-title">Ubiquiti</h1>
                </div>
              </div>
              <div className="ubiquiti-header-meta">
                <small>{t("Updated on")} {convertTimestampToFrenchFormat(new Date(), "fr-FR")}</small>
              </div>
            </div>

            <section className="ubiquiti-dashboard">
              <aside className="ubiquiti-sidebar-panel">
                <UbiquitiGatewaySummary
                  apiUrl={apiUrl}
                  enabled={canLoadCleanPanelData}
                  onQueryStateChange={setGatewaySummaryQueryState}
                  refreshKey={gatewaySummaryRefreshKey}
                  siteId={networkSiteId}
                />

                <UbiquitiIspPanel
                  apiUrl={apiUrl}
                  enabled={canLoadCleanPanelData}
                  livePointEnabled={canLoadInternetLivePoint}
                  onQueryStateChange={setInternetSummaryQueryState}
                  refreshKey={ispPanelRefreshKey}
                  siteId={networkSiteId}
                />

                <UbiquitiDefaultWifiSpeeds
                  apiUrl={apiUrl}
                  enabled={canLoadCleanPanelData}
                  onQueryStateChange={setWifiDefaultSpeedsQueryState}
                  refreshKey={wifiSpeedsRefreshKey}
                  siteId={networkSiteId}
                />
              </aside>

              <main className="ubiquiti-main-panel">
                <UbiquitiGraphToolbar
                  activeAirtimeAp={activeAirtimeAp}
                  activeAirtimeRadio={activeAirtimeRadio}
                  activeView={activeView}
                  activeWifiChartMode={activeWifiChartMode}
                  activeWifiFrequency={activeWifiFrequency}
                  airtimeApOptions={airtimeApOptions}
                  airtimeRadioOptions={airtimeRadioOptions}
                  currentWan={currentWan}
                  flowsTotals={flowsTotals}
                  isAirtimeFetching={dashboardGraphsQuery.isFetching}
                  setActiveAirtimeAp={setActiveAirtimeAp}
                  setActiveAirtimeRadio={setActiveAirtimeRadio}
                  setActiveView={setActiveView}
                  setActiveWan={setActiveWan}
                  setActiveWifiChartMode={setActiveWifiChartMode}
                  setActiveWifiFrequency={setActiveWifiFrequency}
                  showToolbarSelector={showToolbarSelector}
                  toggleAirtimeSeries={toggleAirtimeSeries}
                  toggleFlowsSeries={toggleFlowsSeries}
                  toggleInternetActivity={toggleInternetActivity}
                  toggleInternetSeries={toggleInternetSeries}
                  toggleWifiSeries={toggleWifiSeries}
                  usesIspInternetMetrics={usesIspInternetMetrics}
                  visibleAirtimeSeries={visibleAirtimeSeries}
                  visibleFlowsSeries={visibleFlowsSeries}
                  visibleInternetSeries={visibleInternetSeries}
                  visibleWifiSeries={visibleWifiSeries}
                  wanRows={wanRows}
                  wifiFrequencyOptions={wifiFrequencyOptions}
                />

                <UbiquitiGraphPanel
                  activeChartMode={activeChartMode}
                  activeView={activeView}
                  chartRows={chartRows}
                  healthScore={healthScore}
                  isChartFetching={isChartFetching}
                  isChartLoading={isChartLoading}
                  isChartUpdating={isChartUpdating}
                  isRefreshing={isRefreshing}
                  onRefresh={handleRefresh}
                  theme={theme}
                  visibleSeries={activeGraphVisibleSeries}
                  wifiConnectivityScore={wifiConnectivityScore}
                  wifiLeftAxisLabel={wifiLeftAxisLabel}
                />

                <UbiquitiTopOverview
                  apiUrl={apiUrl}
                  enabled={canLoadCleanPanelData}
                  onQueryStateChange={setTopOverviewQueryState}
                  refreshKey={topOverviewRefreshKey}
                  siteId={networkSiteId}
                />
                <UbiquitiMostCommonDevices
                  apiUrl={apiUrl}
                  enabled={canLoadCleanPanelData}
                  onQueryStateChange={setMostCommonDevicesQueryState}
                  refreshKey={mostCommonDevicesRefreshKey}
                  siteId={networkSiteId}
                />
                <UbiquitiWifiDiagnostics
                  activeFrequency={activeApRadioFrequency}
                  apRows={displayedApRadioRows}
                  txRetryBuckets={txRetryBuckets}
                  connectivityRows={connectivityRows}
                  connectivitySuccess={connectivitySuccess}
                  densityMarkers={densityMarkers}
                  densityScale={densityScale}
                  densityStatus={densityStatus}
                  frequencyProfile={frequencyProfile}
                  isLoading={isDiagnosticsLoading}
                  setActiveFrequency={setActiveApRadioFrequency}
                />
              </main>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
